import React from "react";
import { cn } from "../../utils";
import { CheckboxProps, CheckboxGroupProps } from "./types";
import { isEmpty } from "lodash-es";

export const CheckboxGroup = React.forwardRef<
  HTMLDivElement,
  CheckboxGroupProps
>((props, ref) => {
  const {
    value = [],
    defaultValue = [],
    onValueChange: propsOnValueChange,
    disabled = false,
    readOnly = false,
    className: propsClassName,
    direction = "vertical",
    onChange,
    children,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = React.useState<string[]>(
    isEmpty(value) ? defaultValue : value,
  );

  const isHorizontalDirection = direction === "horizontal";

  // Use controlled value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue;

  const onValueChange = (checkboxValue: string, checked: boolean) => {
    if (readOnly) return;

    const newValue = checked
      ? [...currentValue, checkboxValue]
      : currentValue.filter(v => v !== checkboxValue);

    // Update internal state if uncontrolled
    if (value === undefined) {
      setInternalValue(newValue);
    }

    // Trigger onChange for form integration (react-hook-form)
    onChange?.({
      target: { value: newValue },
    } as unknown as React.ChangeEvent<HTMLDivElement>);

    // Trigger onValueChange callback
    propsOnValueChange?.(newValue);
  };

  const checkboxGroupClassName = cn(
    "qodo qodo-custom-checkbox-group",
    "flex",
    {
      "flex-row gap-6": isHorizontalDirection,
      "flex-col gap-3": !isHorizontalDirection,
    },
    propsClassName,
  );

  // Render children with injected props
  // This function maps over the children and injects necessary props for each checkbox item
  const renderChildren = () => {
    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child;

      const childName = (child.props as { name?: string }).name;
      if (!childName) return child;

      const childDisabled = (child.props as { disabled?: boolean }).disabled;
      const isDisabled = readOnly || disabled || childDisabled;

      return React.cloneElement(child as React.ReactElement<CheckboxProps>, {
        checked: currentValue.includes(childName),
        onCheckedChange: (checked: boolean) =>
          onValueChange(childName, checked),
        disabled: isDisabled,
        name: childName,
      });
    });
  };

  return (
    <div ref={ref} className={checkboxGroupClassName} {...rest}>
      {renderChildren()}
    </div>
  );
});

CheckboxGroup.displayName = "CheckboxGroupComponent";
